import { PluggableStorage } from '@frontend/auth-helpers';
import { sentry } from '@frontend/tracking';

export const REDIRECT_LOOP_THRESHOLD = 10; // max redirects
export const REDIRECT_LOOP_TIMEFRAME_MS = 20000; // timeframe in ms
export const REDIRECT_LOOP_STORAGE_KEY = 'redirectTimestamps';
export const store = PluggableStorage.create('navigateToUrl', sessionStorage);

/**
 * Top level page navigation (as opposed to a react router based nav) with logging.
 * This should be the only place in gatekeeper that sets window.location.href.
 *
 * The auth flow is complex and there's potential to have too many redirects
 * or get stuck in a loop, so we log all top level navigations to help debug
 * any issues.
 */
export function navigateToUrl(
  url: string,
  opts?: { replace?: boolean; reason?: string; error?: Error | string | unknown }
) {
  const currentUrl = window.location.href;
  const currentUrlSanitized = sanitizeUrl(currentUrl);
  const nextUrlSanitized = sanitizeUrl(url);
  const logStr = `Top level navigation from ${currentUrlSanitized} to ${nextUrlSanitized}. Reason: ${
    opts?.reason ?? ''
  }`;
  console.log(logStr);
  sentry.addBreadcrumb({
    category: 'navigateToUrl',
    message: logStr,
  });

  // Infinite redirect check
  const { isInfiniteLoop, redirects } = isInfiniteRedirectLoop(url);
  if (isInfiniteLoop) {
    console.error('Blocked navigation due to suspected infinite redirect loop.', { redirects, url, currentUrl });
    sentry.error({
      error: new Error('Blocked navigation due to suspected infinite redirect loop'),
      topic: 'auth',
      team: 'users-infra',
      sampleRate: 1,
      addContext: {
        name: 'navigateToUrl',
        context: {
          currentUrl: currentUrlSanitized,
          nextUrl: nextUrlSanitized,
          reason: opts?.reason,
          redirectTimestamps: redirects.map((r) => r.url).join(', '),
        },
      },
    });
    throw new Error('Blocked navigation due to suspected infinite redirect loop');
  }

  // This can be really high-volume — expected auth-flow recovery (expired challenge, network blip, etc.)
  if (opts?.error) {
    sentry.warn({
      error: formatErrorForSentry(opts.error),
      topic: 'auth',
      team: 'users-infra',
      addContext: {
        name: 'navigateToUrl',
        context: {
          currentUrl: currentUrlSanitized,
          nextUrl: nextUrlSanitized,
          reason: opts?.reason,
        },
      },
    });
  }

  if (opts?.replace) {
    window.location.replace(url);
  } else {
    window.location.href = url;
  }
}

function isInfiniteRedirectLoop(url: string) {
  let redirects = store.getItem<{ timestamp: number; url: string }[]>(REDIRECT_LOOP_STORAGE_KEY, []);
  const now = Date.now();
  // Only keep timestamps from the last 20 seconds
  redirects = redirects.filter((redirect) => now - redirect.timestamp < REDIRECT_LOOP_TIMEFRAME_MS);
  const nextUrl = safeURL(url);
  redirects.push({ timestamp: now, url: nextUrl.host + nextUrl.pathname }); // store host+path only to avoid PII in logs
  store.setItem(REDIRECT_LOOP_STORAGE_KEY, redirects);

  const isInfiniteLoop = redirects.length > REDIRECT_LOOP_THRESHOLD;
  if (isInfiniteLoop) {
    // clear storage if we are blocking to avoid blocking again on next load
    store.removeItem(REDIRECT_LOOP_STORAGE_KEY);
  }

  return {
    isInfiniteLoop,
    redirects,
  };
}

function safeURL(url: string): URL {
  try {
    return new URL(url, window.location.origin);
  } catch (error) {
    return new URL('about:blank');
  }
}

/**
 * This helps with sidestepping getting PII auto-redacted in Sentry while still allowing us to log the URL structure for debugging purposes.
 * For logging only, will not result in a valid url.
 */
function sanitizeUrl(urlStr: string): string {
  const paramsToRedact = [
    'username',
    'login_challenge',
    'challenge',
    'logout_challenge',
    'logout_verifier',
    'consent_challenge',
    'id_token_hint',
    'state',
    'code',
  ];
  const redactionPlaceholder = '***';

  const url = safeURL(urlStr);
  url.searchParams.forEach((_, key) => {
    if (paramsToRedact.includes(key)) {
      url.searchParams.set(key, redactionPlaceholder);
    }
  });

  return url.toString();
}

function formatErrorForSentry(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}
